<template>
  <ComponentHeader title="User Assignments" subtitle="Assign entity types to users and manage user-entity relationships">
      <button
        @click="showBulkAssignModal = true"
        class="btn-secondary"
      >
        <font-awesome-icon :icon="['fas', 'users']" />
        Bulk Assign
      </button>
      <button
        @click="openAssignModal"
        class="btn-primary"
      >
        <font-awesome-icon :icon="['fas', 'plus']" />
        Assign Entity to User
      </button>
    </ComponentHeader>

  <div class="p-4">
    <!-- Search and Filter Bar -->
    <div class="bg-white p-4 rounded-lg border border-gray-100 mb-2">
      <div class="flex gap-4">
        <input
          v-model="searchQuery"
          placeholder="Search users..."
          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4"
        />
        <select
          v-model="filterType"
          class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4"
        >
          <option value="all">All Users</option>
          <option value="with_entities">Users with Entities</option>
          <option value="without_entities">Users without Entities</option>
        </select>
      </div>
    </div>

    <!-- Users Table -->
    <div class="bg-white rounded-lg border border-gray-100 overflow-hidden mb-2">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              User
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Entity Assignments
            </th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="user in paginatedUsers" :key="user.id">
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm font-medium text-gray-900">
                {{ user.first_name }} {{ user.last_name }}
              </div>
              <div class="text-sm text-gray-500">
                @{{ user.username }}
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ user.email }}
            </td>
            <td class="px-6 py-4">
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="entity in user.entities"
                  :key="entity.id"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {{ entity.entity_type__type_name }}
                  <button
                    @click="removeEntity(user, entity)"
                    class="ml-1 text-blue-600 hover:text-red-600"
                    title="Remove this entity"
                  >
                    <font-awesome-icon :icon="['fas', 'times']" class="text-xs" />
                  </button>
                </span>
                <span
                  v-if="user.entities.length === 0"
                  class="text-sm text-gray-400 italic"
                >
                  No entities assigned
                </span>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <button
                @click="openAssignModalForUser(user)"
                class="text-primary-500 hover:text-primary-700"
              >
                <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-1" />
                Add Entity
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty State -->
      <div v-if="filteredUsers.length === 0" class="text-center py-12">
        <font-awesome-icon :icon="['fas', 'users']" class="text-4xl text-gray-300 mb-3" />
        <p class="text-gray-500">No users found</p>
      </div>
    </div>

    <!-- Pagination Controls -->
    <div v-if="filteredUsers.length > 0" class="bg-white px-4 py-3 rounded-lg flex items-center justify-between border border-gray-100">
      <div class="flex-1 max-sm:flex justify-between sm:hidden">
        <button
          @click="currentPage--"
          :disabled="currentPage === 1"
          class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          @click="currentPage++"
          :disabled="currentPage === totalPages"
          class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
      <div class="max-sm:hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700">
            Showing
            <span class="font-medium">{{ (currentPage - 1) * itemsPerPage + 1 }}</span>
            to
            <span class="font-medium">{{ Math.min(currentPage * itemsPerPage, filteredUsers.length) }}</span>
            of
            <span class="font-medium">{{ filteredUsers.length }}</span>
            users
          </p>
        </div>
        <div>
          <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              @click="currentPage--"
              :disabled="currentPage === 1"
              class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span class="sr-only">Previous</span>
              <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </button>
            <template v-for="page in totalPages" :key="page">
              <button
                v-if="page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)"
                @click="currentPage = page"
                :class="[
                  page === currentPage
                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                ]"
              >
                {{ page }}
              </button>
              <span
                v-else-if="page === currentPage - 3 || page === currentPage + 3"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
              >
                ...
              </span>
            </template>
            <button
              @click="currentPage++"
              :disabled="currentPage === totalPages"
              class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span class="sr-only">Next</span>
              <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>

    <!-- Assign Entity Modal -->
    <div
      v-if="showAssignModal"
      class="fixed inset-0 bg-gray-500/75 overflow-y-auto h-full w-full z-50"
      @click.self="closeAssignModal"
    >
      <div class="relative top-20 mx-auto p-5 w-full max-w-md">
        <div class="bg-white rounded-lg shadow-xl p-6">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold text-gray-900">
              Assign Entity to User
            </h3>
            <button
              @click="closeAssignModal"
              class="text-gray-400 hover:text-gray-500"
            >
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>

          <div class="space-y-4">
            <!-- User Selection (if not pre-selected) -->
            <div v-if="!selectedUser">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Select User
              </label>
              <select
                v-model="assignData.appuser_id"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4"
              >
                <option value="">Choose a user...</option>
                <option
                  v-for="user in userAssignments"
                  :key="user.id"
                  :value="user.id"
                >
                  {{ user.first_name }} {{ user.last_name }} ({{ user.username }})
                </option>
              </select>
            </div>

            <!-- Show selected user info -->
            <div v-else class="bg-gray-50 p-3 rounded">
              <p class="text-sm text-gray-600">Assigning to:</p>
              <p class="font-medium">{{ selectedUser.first_name }} {{ selectedUser.last_name }}</p>
            </div>

            <!-- Entity Type Selection -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Entity Type
              </label>
              <select
                v-model="assignData.entity_type_id"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4"
              >
                <option value="">Choose an entity type...</option>
                <option
                  v-for="entityType in availableEntityTypes"
                  :key="entityType.id"
                  :value="entityType.id"
                  :disabled="isEntityTypeAssigned(entityType.id)"
                >
                  {{ entityType.type_name }}
                  <span v-if="isEntityTypeAssigned(entityType.id)">(Already assigned)</span>
                </option>
              </select>
            </div>

            <!-- Link to Existing Entity (shown if unlinked entities exist) -->
            <div v-if="assignData.entity_type_id && unlinkedEntities.length > 0">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Link to Existing Entity
              </label>
              <select
                v-model="assignData.existing_entity_id"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4"
              >
                <option :value="null">-- Create New Entity --</option>
                <option
                  v-for="entity in unlinkedEntities"
                  :key="entity.id"
                  :value="entity.id"
                >
                  {{ entity.name }}
                </option>
              </select>
              <p class="text-xs text-gray-500 mt-1">
                Select an existing entity to link, or leave as "Create New" to create a new one.
              </p>
            </div>

            <!-- Optional Parent Entity (only for new entities) -->
            <div v-if="!assignData.existing_entity_id">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Parent Entity (Optional)
              </label>
              <select
                v-model="assignData.parent_entity_id"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4"
              >
                <option value="">No parent entity</option>
                <option
                  v-for="entity in availableParentEntities"
                  :key="entity.id"
                  :value="entity.id"
                >
                  {{ entity.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="mt-6 flex justify-end space-x-3">
            <button
              @click="closeAssignModal"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              @click="assignEntity"
              :disabled="!assignData.appuser_id || !assignData.entity_type_id"
              class="btn-primary-sm"
            >
              <font-awesome-icon :icon="['fas', 'save']" />
              Assign Entity
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Assign Modal -->
    <div
      v-if="showBulkAssignModal"
      class="fixed inset-0 bg-gray-500/75 overflow-y-auto h-full w-full z-50"
      @click.self="closeBulkAssignModal"
    >
      <div class="relative top-20 mx-auto p-5 w-full max-w-2xl">
        <div class="bg-white rounded-lg shadow-xl p-6">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold text-gray-900">
              Bulk Assign Entity Type
            </h3>
            <button
              @click="closeBulkAssignModal"
              class="text-gray-400 hover:text-gray-500"
            >
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>

          <div class="space-y-4">
            <!-- Entity Type Selection -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Entity Type to Assign
              </label>
              <select
                v-model="bulkAssignData.entity_type_id"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4"
              >
                <option value="">Choose an entity type...</option>
                <option
                  v-for="entityType in availableEntityTypes"
                  :key="entityType.id"
                  :value="entityType.id"
                >
                  {{ entityType.type_name }}
                </option>
              </select>
            </div>

            <!-- User Selection -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Select Users ({{ bulkAssignData.appuser_ids.length }} selected)
              </label>
              <div class="border border-gray-300 rounded-md max-h-60 overflow-y-auto">
                <label
                  v-for="user in usersWithoutSelectedType"
                  :key="user.id"
                  class="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    :value="user.id"
                    v-model="bulkAssignData.appuser_ids"
                    class="rounded border-gray-300 text-primary-500 focus:ring-blue-dark"
                  />
                  <span class="ml-2 text-sm">
                    {{ user.first_name }} {{ user.last_name }} ({{ user.username }})
                  </span>
                </label>
                <div v-if="usersWithoutSelectedType.length === 0" class="p-3 text-sm text-gray-500 text-center">
                  <span v-if="!bulkAssignData.entity_type_id">Select an entity type first</span>
                  <span v-else>All users already have this entity type</span>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-6 flex justify-end space-x-3">
            <button
              @click="closeBulkAssignModal"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              @click="bulkAssign"
              :disabled="!bulkAssignData.entity_type_id || bulkAssignData.appuser_ids.length === 0"
              class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <font-awesome-icon :icon="['fas', 'users']" class="mr-1" />
              Assign to {{ bulkAssignData.appuser_ids.length }} Users
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faTimes, faPlus, faSave, faUsers, faPlusCircle
} from '@fortawesome/free-solid-svg-icons';

library.add(faTimes, faPlus, faSave, faUsers, faPlusCircle);

export default {
  name: 'UserEntityAssignments',
  components: {
    FontAwesomeIcon,
    ComponentHeader
  },
  data() {
    return {
      userAssignments: [],
      availableEntityTypes: [],
      availableParentEntities: [],
      searchQuery: '',
      filterType: 'all',
      showAssignModal: false,
      showBulkAssignModal: false,
      selectedUser: null,
      assignData: {
        appuser_id: '',
        entity_type_id: '',
        existing_entity_id: null,
        parent_entity_id: null
      },
      unlinkedEntities: [],
      bulkAssignData: {
        entity_type_id: '',
        appuser_ids: []
      },
      loading: false,
      currentPage: 1,
      itemsPerPage: 50
    };
  },
  computed: {
    filteredUsers() {
      let filtered = this.userAssignments;

      // Apply search
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(user => {
          return (
            user.first_name?.toLowerCase().includes(query) ||
            user.last_name?.toLowerCase().includes(query) ||
            user.username?.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query)
          );
        });
      }

      // Apply filter type
      if (this.filterType === 'with_entities') {
        filtered = filtered.filter(user => user.entities.length > 0);
      } else if (this.filterType === 'without_entities') {
        filtered = filtered.filter(user => user.entities.length === 0);
      }

      return filtered;
    },

    paginatedUsers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredUsers.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredUsers.length / this.itemsPerPage);
    },

    usersWithoutSelectedType() {
      if (!this.bulkAssignData.entity_type_id) return [];

      return this.userAssignments.filter(user => {
        return !user.entities.some(e =>
          e.entity_type__id === parseInt(this.bulkAssignData.entity_type_id)
        );
      });
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1; // Reset to first page when searching
    },
    filterType() {
      this.currentPage = 1; // Reset to first page when filtering
    },
    'assignData.entity_type_id'(newTypeId) {
      // Reset existing entity selection when type changes
      this.assignData.existing_entity_id = null;

      if (newTypeId) {
        this.fetchUnlinkedEntities(newTypeId);
        this.fetchAvailableParentEntities(newTypeId);
      } else {
        this.unlinkedEntities = [];
        this.availableParentEntities = [];
      }
    }
  },
  mounted() {
    this.fetchUserAssignments();
    this.fetchAvailableEntityTypes();
  },
  methods: {
    async fetchUserAssignments() {
      this.loading = true;
      try {
        const response = await axios.get('/api/entities/appuser-assignments/');
        this.userAssignments = response.data;
      } catch (error) {
        console.error('Error fetching user assignments:', error);
        this.showError('Failed to load user assignments');
      } finally {
        this.loading = false;
      }
    },

    async fetchAvailableEntityTypes() {
      try {
        const response = await axios.get('/api/entities/user-assignable-types/');
        this.availableEntityTypes = response.data;
      } catch (error) {
        console.error('Error fetching entity types:', error);
      }
    },

    async fetchAvailableParentEntities(entityTypeId) {
      if (!entityTypeId) {
        this.availableParentEntities = [];
        return;
      }

      try {
        const response = await axios.get('/api/entities/', {
          params: {
            entity_type_id: entityTypeId,
            limit: 1000
          }
        });
        this.availableParentEntities = response.data.results || response.data;
      } catch (error) {
        console.error('Error fetching parent entities:', error);
        this.availableParentEntities = [];
      }
    },

    async fetchUnlinkedEntities(entityTypeId) {
      if (!entityTypeId) {
        this.unlinkedEntities = [];
        return;
      }

      try {
        const response = await axios.get('/api/entities/unlinked-entities/', {
          params: { entity_type_id: entityTypeId }
        });
        this.unlinkedEntities = response.data;
      } catch (error) {
        console.error('Error fetching unlinked entities:', error);
        this.unlinkedEntities = [];
      }
    },

    openAssignModal() {
      this.showAssignModal = true;
      // Parent entities will be fetched when entity type is selected via watcher
    },

    openAssignModalForUser(user) {
      this.selectedUser = user;
      this.assignData.appuser_id = user.id;
      this.showAssignModal = true;
      // Parent entities will be fetched when entity type is selected via watcher
    },

    closeAssignModal() {
      this.showAssignModal = false;
      this.selectedUser = null;
      this.assignData = {
        appuser_id: '',
        entity_type_id: '',
        existing_entity_id: null,
        parent_entity_id: null
      };
      this.unlinkedEntities = [];
      this.availableParentEntities = [];
    },

    closeBulkAssignModal() {
      this.showBulkAssignModal = false;
      this.bulkAssignData = {
        entity_type_id: '',
        appuser_ids: []
      };
    },

    isEntityTypeAssigned(entityTypeId) {
      if (!this.selectedUser) return false;
      return this.selectedUser.entities.some(e =>
        e.entity_type__id === entityTypeId
      );
    },

    async assignEntity() {
      try {
        const response = await axios.post('/api/entities/assign-to-user/', this.assignData);

        if (response.data.success) {
          await this.fetchUserAssignments();
          this.closeAssignModal();
          this.showSuccess(response.data.message);
        }
      } catch (error) {
        console.error('Error assigning entity:', error);
        this.showError(error.response?.data?.error || 'Failed to assign entity');
      }
    },

    async removeEntity(user, entity) {
      if (!confirm(`Remove ${entity.entity_type__type_name} entity from ${user.first_name} ${user.last_name}?\n\nThis will delete the entity and all its relationships.`)) {
        return;
      }

      try {
        const response = await axios.delete(`/api/entities/${entity.id}/remove-user-assignment/`);

        if (response.data.success) {
          await this.fetchUserAssignments();
          this.showSuccess(response.data.message);
        }
      } catch (error) {
        console.error('Error removing entity:', error);
        this.showError(error.response?.data?.error || 'Failed to remove entity');
      }
    },

    async bulkAssign() {
      try {
        const response = await axios.post('/api/entities/bulk-assign/', this.bulkAssignData);

        if (response.data.success) {
          await this.fetchUserAssignments();
          this.closeBulkAssignModal();

          // Show summary
          const summary = response.data.summary;
          let message = `Bulk assignment complete:\nCreated: ${summary.created}`;
          if (summary.skipped > 0) {
            message += `\nSkipped: ${summary.skipped}`;
          }
          if (summary.errors > 0) {
            message += `\nErrors: ${summary.errors}`;
          }
          this.showSuccess(message);
        }
      } catch (error) {
        console.error('Error bulk assigning:', error);
        this.showError(error.response?.data?.error || 'Failed to bulk assign');
      }
    },

    showSuccess(message) {
      // You can replace this with a toast library
      alert(`✅ ${message}`);
    },

    showError(message) {
      // You can replace this with a toast library
      alert(`❌ ${message}`);
    }
  }
};
</script>
